import { Schema } from "@effect/schema";
import { Amount, GraphAddress, ProdProtocolKeyService } from "@silo/types";
import { Effect } from "effect";
import { request } from "./request";
import { PercentFromPointsString, UsdFromDecimalString } from "@silo/utils";
import { GraphError } from "./types";

export function getGraphMarkets(): Effect.Effect<
  readonly GraphMarket[],
  GraphError,
  ProdProtocolKeyService
> {
  return Effect.gen(function* () {
    const { silos } = yield* request({
      query,
      schema: GraphMarketsResponse,
    });

    return silos;
  });
}

const query = `#graphql
  {
    silos(
      orderBy: totalValueLockedUSD
      orderDirection: desc
      where: {
        baseAsset_: { activeOracle_not: "null" }
      }
    ) {
      marketAddress: id
      isDeprecated: archived
      tvlUsd: totalValueLockedUSD
      baseAsset {
        tokenAddress: id
      }
      bridgeAssets: bridgeAsset(orderBy: lastPriceUSD, orderDirection: desc) {
        tokenAddress: id
      }
      marketAssets: market {
        inputToken {
          tokenAddress: id
          rawSymbol: symbol
          rawName: name
          decimals
        }
        priceUsd: tokenPriceUSD
        liquidity: balance
        rates {
          side
          rate
        }
        depositedTotal: supply
        protectedTotal: protectedSupply
        debtTotal: borrowed
        sToken {
          collateralShareToken: id
        }
        spToken {
          protectedShareToken: id
        }
        dToken {
          debtShareToken: id
        }
        isPaused
      }
    }
  }
`;

export type GraphMarket = typeof GraphMarket.Type;
export type GraphMarketEncoded = typeof GraphMarket.Encoded;

export const GraphMarket = Schema.Struct({
  marketAddress: GraphAddress,
  isDeprecated: Schema.Boolean,
  tvlUsd: UsdFromDecimalString,
  baseAsset: Schema.Struct({
    tokenAddress: GraphAddress,
  }),
  bridgeAssets: Schema.Tuple(
    Schema.Struct({
      tokenAddress: GraphAddress,
    }),
    Schema.optionalElement(
      Schema.Struct({
        tokenAddress: GraphAddress,
      })
    )
  ),
  marketAssets: Schema.Array(
    Schema.Struct({
      inputToken: Schema.Struct({
        tokenAddress: GraphAddress,
        rawName: Schema.String,
        rawSymbol: Schema.String,
        decimals: Schema.Number,
      }),
      priceUsd: UsdFromDecimalString,
      liquidity: Amount,
      rates: Schema.Array(
        Schema.Struct({
          rate: PercentFromPointsString,
          side: Schema.Literal("LENDER", "BORROWER"),
        })
      ),
      depositedTotal: Amount,
      protectedTotal: Amount,
      debtTotal: Amount,
      sToken: Schema.Struct({
        collateralShareToken: GraphAddress,
      }),
      spToken: Schema.Struct({
        protectedShareToken: GraphAddress,
      }),
      dToken: Schema.Struct({
        debtShareToken: GraphAddress,
      }),
      isPaused: Schema.Boolean,
    })
  ),
}).annotations({ identifier: "GraphMarket" });

export type GraphMarketsResponse = typeof GraphMarketsResponse.Type;
export type GraphMarketsResponseEncoded = typeof GraphMarketsResponse.Encoded;

export const GraphMarketsResponse = Schema.Struct({
  silos: Schema.Array(GraphMarket),
}).annotations({ identifier: "GraphMarketsResponse" });
